import { isGarmentTypeAllowedForModel } from './modelStudioConfig';

export const MODEL_TRY_ON_PRESETS_PER_COMBO = 3;

export const MODEL_TRY_ON_PRESET_ASSET_BASE = '/assets/model-try-on-presets';

/** 展示优先扩展名（体积更小）；`<img>` 失败时可回退 {@link MODEL_TRY_ON_PRESET_ASSET_FALLBACK_EXT} */
export const MODEL_TRY_ON_PRESET_ASSET_EXT = 'webp';
export const MODEL_TRY_ON_PRESET_ASSET_FALLBACK_EXT = 'png';

/** public/assets 下静态模特图实际落盘扩展名（提交后端须与此一致，勿固定 webp） */
export const MODEL_TRY_ON_PRESET_DISK_EXT = 'png';

/** 静态资源版本号：更新预设图后递增，用于穿透浏览器缓存 */
export const MODEL_TRY_ON_PRESET_ASSET_REV = '20260720a';

/** 系统模特参考图 · 3 张依次对应人种 */
export const MODEL_TRY_ON_PRESET_ETHNICITY_LABELS = ['黄种人', '白人', '黑人'];

/** 系统模特参考图 · 3 种 lookbook 姿势标签 */
export const MODEL_TRY_ON_PRESET_POSE_LABELS = ['正面站立', '侧身迈步', '休闲坐姿'];

/** 静态资源命名：{modelType}-{garmentType}-{index}-{ethnicity}.webp（兼容历史 .png） */
export const MODEL_TRY_ON_TRIPLE_IMPORT_SLOTS = [
  { index: 1, ethnicity: 'asian', suffix: 'asian' },
  { index: 2, ethnicity: 'white', suffix: 'white' },
  { index: 3, ethnicity: 'black', suffix: 'black' },
];

const MODEL_TRY_ON_PRESET_SLOTS = MODEL_TRY_ON_TRIPLE_IMPORT_SLOTS;

export function labelForTryOnPresetEthnicity(ethnicity, index) {
  if (ethnicity === 'white') return '白人';
  if (ethnicity === 'black') return '黑人';
  if (ethnicity === 'asian') return '黄种人';
  const idx = Math.max(0, Number(index || 1) - 1);
  return MODEL_TRY_ON_PRESET_ETHNICITY_LABELS[idx] || '系统模特';
}

/** 将试衣静态 URL 规范为优先扩展名（png → webp），保留 query */
export function preferTryOnPresetWebpUrl(url) {
  const raw = String(url || '').trim();
  if (!raw || !isTryOnSystemPresetAssetUrl(raw)) return raw;
  const [pathPart, query = ''] = raw.split('?');
  const nextPath = pathPart.replace(/\.png$/i, `.${MODEL_TRY_ON_PRESET_ASSET_EXT}`);
  return query ? `${nextPath}?${query}` : nextPath;
}

/** webp 加载失败时回退到同名 png（保留 query） */
export function resolveTryOnPresetPngFallbackUrl(url) {
  const raw = String(url || '').trim();
  if (!raw || !isTryOnSystemPresetAssetUrl(raw)) return '';
  const [pathPart, query = ''] = raw.split('?');
  if (!/\.webp$/i.test(pathPart)) return '';
  const nextPath = pathPart.replace(/\.webp$/i, `.${MODEL_TRY_ON_PRESET_ASSET_FALLBACK_EXT}`);
  return query ? `${nextPath}?${query}` : nextPath;
}

/** 构建单张静态系统模特图相对路径（扩展名与磁盘文件一致，默认 {@link MODEL_TRY_ON_PRESET_DISK_EXT}） */
export function buildTryOnPresetAssetPath(
  modelType,
  garmentType,
  index,
  ethnicitySuffix,
  ext = MODEL_TRY_ON_PRESET_DISK_EXT,
) {
  const model = String(modelType || '').trim();
  const garment = String(garmentType || '').trim();
  if (!model || !garment) return '';
  const normalizedExt = String(ext || MODEL_TRY_ON_PRESET_DISK_EXT).trim().toLowerCase();
  return `${MODEL_TRY_ON_PRESET_ASSET_BASE}/${model}/${garment}/${model}-${garment}-${index}-${ethnicitySuffix}.${normalizedExt}`;
}

/** 将静态 preset URL 的扩展名改为目标后缀（保留 query） */
export function rewriteTryOnStaticPresetUrlToExtension(url, ext) {
  const raw = String(url || '').trim();
  if (!raw || !isTryOnSystemPresetAssetUrl(raw)) return raw;
  const normalizedExt = String(ext || MODEL_TRY_ON_PRESET_DISK_EXT).trim().toLowerCase();
  if (!['webp', 'png', 'jpg', 'jpeg'].includes(normalizedExt)) return raw;
  const [pathPart, query = ''] = raw.split('?');
  const stem = pathPart.replace(/\.(webp|png|jpe?g)$/i, '');
  if (!stem) return raw;
  const fileExt = normalizedExt === 'jpeg' ? 'jpg' : normalizedExt;
  const nextPath = `${stem}.${fileExt}`;
  return query ? `${nextPath}?${query}` : nextPath;
}

/**
 * 提交后端 / 任务持久化用的静态模特 URL（扩展名与磁盘一致，非展示用 webp）。
 * 始终落盘扩展名 {@link MODEL_TRY_ON_PRESET_DISK_EXT}；后端 Img2ImgImageResolver 会 png↔webp 回退。
 * @param {{ url?: string, apiUrl?: string, assetExt?: string } | string} presetOrUrl
 */
export function resolveTryOnPresetSubmitUrl(presetOrUrl) {
  if (presetOrUrl && typeof presetOrUrl === 'object') {
    const apiUrl = String(presetOrUrl.apiUrl || '').trim();
    if (apiUrl) {
      if (isTryOnSystemPresetAssetUrl(apiUrl)) {
        return rewriteTryOnStaticPresetUrlToExtension(apiUrl, MODEL_TRY_ON_PRESET_DISK_EXT);
      }
      return apiUrl;
    }
    const url = String(presetOrUrl.url || '').trim();
    if (!url) return '';
    if (!isTryOnSystemPresetAssetUrl(url)) return url;
    return rewriteTryOnStaticPresetUrlToExtension(url, MODEL_TRY_ON_PRESET_DISK_EXT);
  }
  const url = String(presetOrUrl || '').trim();
  if (!url || !isTryOnSystemPresetAssetUrl(url)) return url;
  return rewriteTryOnStaticPresetUrlToExtension(url, MODEL_TRY_ON_PRESET_DISK_EXT);
}

/** 按模特性别 + 衣物类型返回 3 张本地系统模特（public/assets） */
export function buildStaticTryOnPresets(modelType, garmentType) {
  const model = String(modelType || '').trim();
  const garment = String(garmentType || '').trim();
  if (!model || !garment) return [];
  if (!isGarmentTypeAllowedForModel(garment, model)) return [];

  return MODEL_TRY_ON_PRESET_SLOTS.map(({ index, ethnicity, suffix }) => {
    const assetExt = MODEL_TRY_ON_PRESET_DISK_EXT;
    const url = buildTryOnPresetAssetPath(model, garment, index, suffix, assetExt);
    return {
      id: `${model}-${garment}-${index}`,
      modelType: model,
      garmentType: garment,
      ethnicity,
      index,
      url,
      assetExt,
      source: 'static',
    };
  });
}

/** 将预设图地址转为前端可展示的完整 URL */
export function resolveTryOnPresetPublicUrl(relativePath) {
  const path = String(relativePath || '').trim();
  if (!path) return '';
  return path || '';
}

/** 系统模特静态图展示地址（默认优先 webp；附带版本参数，避免浏览器长期缓存旧图） */
export function buildTryOnPresetDisplayUrl(preset, { preferWebp = true } = {}) {
  const url = String(preset?.url || '').trim();
  if (!url || !isTryOnSystemPresetAssetUrl(url)) return '';
  const preferred = preferWebp ? preferTryOnPresetWebpUrl(url) : url;
  const base = resolveTryOnPresetPublicUrl(preferred);
  if (!base) return '';
  if (/[?&]v=/.test(base)) return base;
  const rev = preset?.assetRev || MODEL_TRY_ON_PRESET_ASSET_REV;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}v=${encodeURIComponent(String(rev))}`;
}

/** 是否为系统模特静态资源地址 */
export function isTryOnSystemPresetAssetUrl(url) {
  const s = String(url || '').trim();
  if (!s) return false;
  return s.includes(`${MODEL_TRY_ON_PRESET_ASSET_BASE}/`) || s.includes('model-try-on-presets/');
}

/** 按 presetId 查找当前模特性别 + 衣物类型下的系统模特 */
export function findTryOnPresetById(modelType, garmentType, presetId) {
  const id = String(presetId || '').trim();
  if (!id) return null;
  return buildStaticTryOnPresets(modelType, garmentType).find((item) => String(item.id) === id) || null;
}

/** 从工作流/任务参数还原已选系统模特：请从 tryOnSystemModelPresetFallback 导入 async/sync 版本。 */
export function resolveTryOnPresetFromParams(modelType, garmentType, params = {}, presets = []) {
  const presetId = String(params?.selectedPresetId || '').trim();
  const savedUrl = String(params?.selectedPresetUrl || '').trim();
  if (!presetId && !savedUrl) return null;
  if (presetId) {
    const byId = findTryOnPresetById(modelType, garmentType, presetId);
    if (byId) return byId;
  }
  if (savedUrl) {
    return {
      id: presetId,
      modelType,
      garmentType,
      url: savedUrl,
      source: 'saved',
    };
  }
  return null;
}

/** 从系统模特资源 URL 反推 presetId，如 female-jeansJogger-1 */
export function inferTryOnPresetIdFromAssetUrl(url) {
  const s = String(url || '').trim();
  const match = s.match(/model-try-on-presets\/([^/]+)\/([^/]+)\/\1-\2-(\d+)-/i);
  if (!match) return '';
  return `${match[1]}-${match[2]}-${match[3]}`;
}

/** 复用/重试时区分「用户上传模特」与「系统模特」 */
export function resolveTryOnReuseModelState(result) {
  const r = result && typeof result === 'object' ? result : {};
  if (r.usedUploadedModel === true) {
    return { usedUploadedModel: true, selectedPresetId: '' };
  }
  const savedPresetId = r.selectedPresetId ? String(r.selectedPresetId).trim() : '';
  if (r.usedUploadedModel === false) {
    return {
      usedUploadedModel: false,
      selectedPresetId: savedPresetId || inferTryOnPresetIdFromAssetUrl(r.modelImageUrl),
    };
  }
  if (savedPresetId) {
    return { usedUploadedModel: false, selectedPresetId: savedPresetId };
  }
  const modelUrl = String(r.modelImageUrl || '').trim();
  if (isTryOnSystemPresetAssetUrl(modelUrl)) {
    return {
      usedUploadedModel: false,
      selectedPresetId: inferTryOnPresetIdFromAssetUrl(modelUrl),
    };
  }
  if (modelUrl && r.hasCustomModel) {
    return { usedUploadedModel: true, selectedPresetId: '' };
  }
  return { usedUploadedModel: false, selectedPresetId: '' };
}

function resolveGarmentPreviewCountFromResult(result) {
  const garmentUrls = Array.isArray(result?.garmentImageUrls) ? result.garmentImageUrls : [];
  if (garmentUrls.length > 0) return garmentUrls.length;
  const selected = Number(result?.selectedGarmentCount);
  if (Number.isFinite(selected) && selected > 0) return selected;
  const previews = Array.isArray(result?.referencePreviews) ? result.referencePreviews : [];
  if (result?.hasCustomModel && previews.length > 0) return Math.max(0, previews.length - 1);
  return previews.length;
}

/** 模特试衣任务/步骤中用于展示的模特参考图（系统预设或用户上传） */
export function resolveModelTryOnModelPreviewUrl(result) {
  const r = result && typeof result === 'object' ? result : {};
  const reuseModel = resolveTryOnReuseModelState(r);
  const previews = Array.isArray(r.referencePreviews) ? r.referencePreviews : [];
  const garmentCount = resolveGarmentPreviewCountFromResult(r);
  const modelFromPreviews = previews[garmentCount] || '';

  if (reuseModel.usedUploadedModel) {
    return String(r.modelImageUrl || modelFromPreviews || '').trim();
  }
  const presetDisplayUrl = String(r.selectedPresetDisplayUrl || r.modelImagePreviewUrl || '').trim();
  if (presetDisplayUrl) return presetDisplayUrl;
  if (!reuseModel.usedUploadedModel) {
    const preview = String(modelFromPreviews || '').trim();
    if (
      preview
      && (
        preview.includes('/api/storage/images/view')
        || preview.includes('/api/storage/view')
        || isTryOnSystemPresetAssetUrl(preview)
        || preview.startsWith('/images/')
      )
    ) {
      return preview;
    }
  }
  const presetUrl = String(r.selectedPresetUrl || '').trim();
  if (presetUrl) return presetUrl;
  if (isTryOnSystemPresetAssetUrl(r.modelImageUrl)) {
    return String(r.modelImageUrl).trim();
  }
  return String(modelFromPreviews || '').trim();
}

/** 仅系统模特参考图；用户上传模特时返回空 */
export function resolveModelTryOnSystemModelPreviewUrl(result) {
  const reuseModel = resolveTryOnReuseModelState(result);
  if (reuseModel.usedUploadedModel) return '';
  return resolveModelTryOnModelPreviewUrl(result);
}
